/**
 * AppErrorBoundary (Plan 06-02, Task 1; D-15).
 *
 * Top-level React class component error boundary. Wraps `<RouterProvider>`
 * in `App.tsx` so an uncaught render error ANYWHERE in the tree — including
 * a crash in the i18n provider itself — converts to a generic Phase-6
 * "Noe gikk galt" page instead of React's white blank screen.
 *
 * Why a class:
 *   React 19 still keeps `getDerivedStateFromError` + `componentDidCatch`
 *   as the only supported way to convert child render errors into
 *   fallback UI. Functional `useErrorBoundary` is not standard yet.
 *
 * Why HARDCODED Norwegian strings:
 *   The whole point of being above i18n is that this boundary can NOT
 *   depend on the same i18n that just crashed. The two strings here
 *   match `errors.generic.heading` / `errors.generic.body` verbatim, so
 *   the visual contract is identical when both boundaries fire.
 *
 * T-06-05 (Information Disclosure): the raw `error.message` is logged
 * to `console.error` ONLY — the rendered DOM stays the generic copy.
 */
import { Component, type ErrorInfo, type ReactNode, type JSX } from 'react';
import { ErrorState } from '../primitives';

interface AppErrorBoundaryProps {
  children: ReactNode;
}

interface AppErrorBoundaryState {
  error: Error | null;
}

export class AppErrorBoundary extends Component<
  AppErrorBoundaryProps,
  AppErrorBoundaryState
> {
  state: AppErrorBoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Structured log; raw stack stays out of the DOM (T-06-05).
    console.error('[AppErrorBoundary]', error, info);
  }

  render(): ReactNode | JSX.Element {
    if (this.state.error) {
      return (
        <ErrorState
          variant="page"
          heading="Noe gikk galt"
          body="Prøv igjen, eller last siden på nytt."
          onRetry={() => window.location.reload()}
        />
      );
    }
    return this.props.children;
  }
}
